import LegalPage, { Section, SubSection, LegalList } from "./LegalPage";

export default function PrivacyPolicy() {
  return (
    <LegalPage
      eyebrow="Legal"
      title="Privacy Policy"
      version="1.0"
      effectiveDate="9 February 2026"
      testId="privacy-page"
    >
      <Section number="1" title="Customer Data">
        <SubSection number="1.1" title="Ownership of Customer Data">
          <p>
            Vanguard Beacon recognises that the information stored within the Platform represents an
            important part of the Customer's business operations. As between Vanguard Beacon and the
            Customer, the Customer retains all right, title and interest in and to all Customer Data
            uploaded to, created within, or otherwise processed through the Platform. Nothing in these
            Terms transfers ownership of Customer Data to Vanguard Beacon.
          </p>
        </SubSection>
        <SubSection number="1.2" title="Licence to Process Customer Data">
          <p>
            To provide the Platform and associated services, the Customer grants Vanguard Beacon a
            limited, worldwide, non-exclusive licence to host, store, process, transmit, copy and display
            Customer Data solely for the purpose of:
          </p>
          <LegalList>
            <li>operating the Platform;</li>
            <li>providing requested services;</li>
            <li>maintaining system security;</li>
            <li>generating reports and dashboards;</li>
            <li>providing AI-assisted functionality;</li>
            <li>responding to support requests;</li>
            <li>complying with applicable legal obligations.</li>
          </LegalList>
          <p>This licence continues only for as long as necessary to provide the Services or as otherwise required by law.</p>
        </SubSection>
        <SubSection number="1.3" title="Accuracy of Customer Data">
          <p>
            The Customer is solely responsible for ensuring that Customer Data is accurate, complete and
            up to date. Vanguard Beacon does not verify, audit or validate Customer Data entered into the
            Platform. Reports, reminders, analytics and AI-generated outputs depend on the quality and
            completeness of Customer Data.
          </p>
        </SubSection>
        <SubSection number="1.4" title="Customer Responsibility for Records">
          <p>The Customer remains responsible for:</p>
          <LegalList>
            <li>maintaining legally required records;</li>
            <li>ensuring records are truthful;</li>
            <li>retaining records for applicable statutory periods;</li>
            <li>complying with regulatory recordkeeping requirements.</li>
          </LegalList>
          <p>Use of the Platform does not relieve the Customer of any legal obligation relating to record retention.</p>
        </SubSection>
      </Section>

      <Section number="2" title="Privacy and Personal Data">
        <p>
          Vanguard Beacon is committed to protecting Personal Data and handling such information
          responsibly. Our collection, use and disclosure of Personal Data is governed by this Privacy
          Policy, which forms part of our Terms of Service. Where applicable, Vanguard Beacon will
          comply with the Personal Data Protection Act 2012 (Singapore) ("PDPA") and any other
          applicable data protection laws.
        </p>
        <SubSection number="2.1" title="Customer Responsibilities">
          <p>
            Where the Customer uploads Personal Data relating to employees, contractors, suppliers or
            other individuals, the Customer represents and warrants that it has obtained all necessary
            rights, permissions and consents required by applicable law. The Customer remains
            responsible for ensuring that its collection and use of Personal Data complies with
            applicable legislation.
          </p>
        </SubSection>
        <SubSection number="2.2" title="Data Security">
          <p>
            Vanguard Beacon implements commercially reasonable administrative, technical and
            organisational safeguards designed to protect Customer Data from unauthorised access,
            disclosure, alteration or destruction. However, no online service can guarantee absolute
            security. Accordingly, Vanguard Beacon does not warrant that the Platform will be completely
            free from security risks.
          </p>
        </SubSection>
      </Section>

      <Section number="3" title="Intellectual Property">
        <SubSection number="3.1" title="Ownership of the Platform">
          <p>Vanguard Beacon and its licensors retain all intellectual property rights in and to the Platform, including:</p>
          <LegalList>
            <li>software;</li>
            <li>source code;</li>
            <li>object code;</li>
            <li>AI models;</li>
            <li>workflows;</li>
            <li>databases;</li>
            <li>user interfaces;</li>
            <li>reports;</li>
            <li>documentation;</li>
            <li>branding;</li>
            <li>logos;</li>
            <li>designs;</li>
            <li>graphics;</li>
            <li>proprietary methodologies;</li>
            <li>updates and enhancements.</li>
          </LegalList>
          <p>Except for the limited licence granted under our Terms of Service, no intellectual property rights are transferred to the Customer.</p>
        </SubSection>
        <SubSection number="3.2" title="Customer Content">
          <p>The Customer retains ownership of:</p>
          <LegalList>
            <li>uploaded documents;</li>
            <li>SOPs;</li>
            <li>policies;</li>
            <li>forms;</li>
            <li>audit reports;</li>
            <li>compliance records;</li>
            <li>checklists;</li>
            <li>photographs;</li>
            <li>operational documentation.</li>
          </LegalList>
          <p>Vanguard Beacon claims no ownership over Customer Content.</p>
        </SubSection>
        <SubSection number="3.3" title="Feedback">
          <p>
            The Customer may voluntarily provide suggestions, ideas or feedback regarding the Platform.
            Unless otherwise agreed in writing, Vanguard Beacon may use such feedback to improve the
            Platform without restriction or obligation to compensate the Customer.
          </p>
        </SubSection>
      </Section>

      <Section number="4" title="Confidentiality">
        <p>Both parties acknowledge that they may receive confidential information belonging to the other. Each party agrees to:</p>
        <LegalList>
          <li>keep confidential information secure;</li>
          <li>use confidential information only for purposes related to our Terms of Service;</li>
          <li>not disclose confidential information except where authorised or required by law.</li>
        </LegalList>
        <p>Confidential information does not include information that:</p>
        <LegalList>
          <li>is publicly available;</li>
          <li>was already lawfully known;</li>
          <li>is independently developed without reference to the confidential information;</li>
          <li>is lawfully obtained from another source.</li>
        </LegalList>
        <p>The obligations under this section continue after termination of our Terms of Service.</p>
      </Section>

      <Section number="5" title="Contact Information">
        <p>Questions regarding this Privacy Policy may be directed to:</p>
        <p>
          <strong>Vanguard Beacon</strong>
          <br />
          Email: <a href="mailto:hello@vanguardbeacon.com" className="text-brand-orange hover:underline">hello@vanguardbeacon.com</a>
          <br />
          Website: <a href="https://vanguardbeacon.com" className="text-brand-orange hover:underline">https://vanguardbeacon.com</a>
        </p>
      </Section>
    </LegalPage>
  );
}
